import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Mail, Lock, FileCheck2, AlertCircle, Loader2, MailCheck, Eye, EyeOff } from "lucide-react";
import { Logo } from "@/components/Common";
import { useSeo } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { startGoogleLogin, resolveLanding } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const WHY = [
  { icon: FileCheck2, t: "We create your private workspace and save every step you build." },
  { icon: ShieldCheck, t: "We never post on your behalf. Your work stays yours to keep and export." },
];

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuth();
  const [params] = useSearchParams();
  const [redirecting, setRedirecting] = useState(false);

  const next = params.get("next") || "";
  const errored = params.get("error");
  const expired = params.get("expired");

  // mode: 'signin' | 'signup' | 'forgot'. /signup route defaults to account creation.
  const [mode, setMode] = useState(location.pathname === "/signup" ? "signup" : "signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [panel, setPanel] = useState(null); // 'verify-sent' | 'reset-sent'

  const isSignup = location.pathname === "/signup";
  useSeo({
    title: isSignup ? "Create your account | Launch to Income" : "Log in | Launch to Income",
    description: isSignup
      ? "Create your free Launch to Income workspace and start turning your idea into your first paying customer."
      : "Log in to your Launch to Income workspace and continue building your business.",
    path: isSignup ? "/signup" : "/login",
    noindex: true,
  });

  useEffect(() => {
    if (user) navigate(user.role === "founder" ? "/founder" : user.onboarding_complete ? "/dashboard" : "/onboarding", { replace: true });
  }, [user, navigate]);

  const onGoogle = () => { setRedirecting(true); startGoogleLogin(next); };

  const routeAfterAuth = async (u) => {
    // Hard navigation so the freshly-issued session cookie is picked up by AuthProvider.
    const dest = await resolveLanding(u, next);
    window.location.assign(dest);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    const origin = window.location.origin;
    try {
      if (mode === "signup") {
        await api.post("/auth/register", { name, email, password, origin });
        setPanel("verify-sent");
      } else if (mode === "signin") {
        const { data } = await api.post("/auth/login-email", { email, password });
        setUser(data.user);
        routeAfterAuth(data.user);
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email, origin });
        setPanel("reset-sent");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try { await api.post("/auth/resend-verification", { email, origin: window.location.origin }); } catch { /* ignore */ }
  };

  const switchMode = (m) => { setMode(m); setError(""); setPanel(null); };

  return (
    <div className="flex min-h-screen bg-[#FDFCFE]">
      <div className="hidden w-[44%] flex-col justify-between bg-brand-deep p-12 lg:flex">
        <Logo variant="white" height="h-9" className="self-start" />
        <div>
          <p className="font-heading text-3xl font-bold leading-tight text-white">
          From idea to your first paying customer.
        </p>
          <p className="mt-4 max-w-sm text-brand-mutedHover">
            One guided workspace that walks you step by step, drafts the hard parts with AI, and leaves
            you holding real business assets you own.
          </p>
          <div className="mt-8 space-y-3">
            {WHY.map((w) => (
              <div key={w.t} className="flex items-start gap-3 rounded-xl bg-white/5 px-4 py-3">
                <w.icon size={18} className="mt-0.5 shrink-0 text-brand-muted" strokeWidth={1.9} />
                <span className="text-sm text-white">{w.t}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-brand-muted">Free during early access. No credit card required.</p>
      </div>

      <div className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm ltifade" data-testid="login-screen">
          <div className="lg:hidden"><Logo height="h-9" /></div>

          {panel === "verify-sent" ? (
            <div data-testid="verify-sent-panel" className="mt-6 lg:mt-0">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-surface"><MailCheck size={24} className="text-brand" /></span>
              <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight">Check your inbox</h2>
              <p className="mt-2 text-sm text-slate-600">
                We sent a confirmation link to <strong>{email}</strong>. Click it to activate your workspace.
                The link expires in 24 hours.
              </p>
              <button data-testid="resend-verification-btn" onClick={resend} className="mt-5 text-sm font-medium text-brand hover:underline">Resend the email</button>
              <p className="mt-6 text-sm text-slate-500">
                <button onClick={() => switchMode("signin")} className="font-medium text-brand hover:underline">Back to sign in</button>
              </p>
            </div>
          ) : panel === "reset-sent" ? (
            <div data-testid="reset-sent-panel" className="mt-6 lg:mt-0">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-surface"><MailCheck size={24} className="text-brand" /></span>
              <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight">Check your inbox</h2>
              <p className="mt-2 text-sm text-slate-600">
                If an account exists for <strong>{email}</strong>, we've sent a password reset link. It expires in 1 hour.
              </p>
              <p className="mt-6 text-sm text-slate-500">
                <button onClick={() => switchMode("signin")} className="font-medium text-brand hover:underline">Back to sign in</button>
              </p>
            </div>
          ) : (
            <>
              <h1 className="mt-6 font-heading text-2xl font-bold tracking-tight lg:mt-0">
                {mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Welcome back"}
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                {mode === "signup" ? "Create your private workspace now. Your progress is saved, and every asset remains yours to export."
                  : mode === "forgot" ? "Enter your email and we'll send a reset link."
                  : "Sign in to continue building your business."}
              </p>

              {(errored || expired) && (
                <div data-testid="auth-error" className="mt-5 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{expired ? "Your session expired. Please sign in again." : "That sign in didn't complete. Please try again."}</span>
                </div>
              )}

              {mode !== "forgot" && (
                <>
                  <button data-testid="continue-google-btn" onClick={onGoogle} disabled={redirecting}
                    className="mt-6 flex w-full items-center justify-center gap-3 rounded-xl border border-brand-line bg-white px-4 py-3.5 text-sm font-medium shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lift disabled:opacity-60">
                    <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
                    {redirecting ? "Redirecting to Google…" : "Continue with Google"}
                  </button>
                  <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
                    <span className="h-px flex-1 bg-brand-line" /> or <span className="h-px flex-1 bg-brand-line" />
                  </div>
                </>
              )}

              <form onSubmit={submit} className="space-y-3.5">
                {mode === "signup" && (
                  <div>
                    <label htmlFor="auth-name" className="mb-1 block text-xs font-medium text-slate-600">Your name</label>
                    <input id="auth-name" data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} required
                      autoComplete="name" placeholder="Your name"
                      className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40" />
                  </div>
                )}
                <div>
                  <label htmlFor="auth-email" className="mb-1 block text-xs font-medium text-slate-600">Email address</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input id="auth-email" data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                      autoComplete="email" placeholder="you@example.com"
                      className="w-full rounded-xl border border-brand-line bg-white py-3 pl-10 pr-4 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40" />
                  </div>
                </div>
                {mode !== "forgot" && (
                  <div>
                    <label htmlFor="auth-password" className="mb-1 block text-xs font-medium text-slate-600">Password</label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input id="auth-password" data-testid="auth-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                        autoComplete={mode === "signup" ? "new-password" : "current-password"}
                        placeholder={mode === "signup" ? "8+ characters, a letter and a number" : "Your password"}
                        className="w-full rounded-xl border border-brand-line bg-white py-3 pl-10 pr-11 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40" />
                      <button type="button" data-testid="toggle-password" onClick={() => setShowPw((s) => !s)}
                        aria-label={showPw ? "Hide password" : "Show password"}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-slate-400 hover:text-slate-700">
                        {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {mode === "signup" && <p className="mt-1 text-xs text-slate-400">At least 8 characters, including a letter and a number.</p>}
                  </div>
                )}

                {error && <p data-testid="auth-form-error" className="text-sm text-red-600">{error}</p>}

                <button data-testid="auth-submit-btn" type="submit" disabled={busy}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3.5 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover disabled:opacity-60">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : null}
                  {mode === "signup" ? "Create account" : mode === "forgot" ? "Send reset link" : "Sign in"}
                  {!busy && <ArrowRight size={15} />}
                </button>
              </form>

              <div className="mt-5 space-y-2 text-center text-sm text-slate-500">
                {mode === "signin" && (
                  <>
                    <p><button data-testid="to-forgot-btn" onClick={() => switchMode("forgot")} className="text-brand hover:underline">Forgot your password?</button></p>
                    <p>New here? <button data-testid="to-signup-btn" onClick={() => switchMode("signup")} className="font-medium text-brand hover:underline">Create an account</button></p>
                  </>
                )}
                {mode === "signup" && (
                  <p>Already have an account? <button data-testid="to-signin-btn" onClick={() => switchMode("signin")} className="font-medium text-brand hover:underline">Sign in</button></p>
                )}
                {mode === "forgot" && (
                  <p><button data-testid="to-signin-btn" onClick={() => switchMode("signin")} className="font-medium text-brand hover:underline">Back to sign in</button></p>
                )}
              </div>

              <p className="mt-8 text-center text-xs text-slate-500">
                By continuing you agree to our{" "}
                <Link to="/terms" className="inline-block py-1 font-medium text-slate-700 underline hover:text-brand">Terms</Link> and{" "}
                <Link to="/privacy" className="inline-block py-1 font-medium text-slate-700 underline hover:text-brand">Privacy Policy</Link>.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
